<template>
  <b-card
    no-body
    class="dashboard-graph-card"
  >
    <b-card-header class="w-100 border-0 bg-white graph-header">
      <div class="graph-title-wrap">
        <b-card-title class="mb-0 graph-title">
          Last Updates
        </b-card-title>
      </div>

      <div class="graph-legend">
        <span class="legend-item">
          <span class="dot dot-views" />
          Views: {{ totals.views }}
        </span>
        <span class="legend-item">
          <span class="dot dot-clicks" />
          Clicks: {{ totals.clicks }}
        </span>
        <span class="legend-item">
          <span class="dot dot-followers" />
          Followers: {{ totals.followers }}
        </span>
      </div>

      <div class="graph-actions">
        <v-select
          v-model="selectedAdOption"
          :options="adOptions"
          :clearable="false"
          class="action-input select-ad"
          label="name"
        >
          <template #selected-option="{ name, logo }">
            <div class="d-flex align-items-center">
              <b-avatar
                :src="logo"
                size="22"
                class="mr-50"
              />
              <span>{{ name }}</span>
            </div>
          </template>
          <template #option="{ name, logo }">
            <div class="d-flex align-items-center">
              <b-avatar
                :src="logo"
                size="22"
                class="mr-50"
              />
              <span>{{ name }}</span>
            </div>
          </template>
        </v-select>
        <vendor-date-picker
          v-model="selectedDate"
          class="action-input date-input"
          placeholder="MM/DD/YYYY"
        />
      </div>
    </b-card-header>

    <b-card-body
      class="graph-chart-body pt-0 graph-click-area"
      @click="openOverallDetails"
    >
      <vue-apex-charts
        type="area"
        height="330"
        :options="chartOptions"
        :series="series"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BCardTitle,
  BAvatar,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import vSelect from 'vue-select'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import VendorDatePicker from '@/components/vendor/VendorDatePicker.vue'
import { resolveMediaUrl } from '@/utils/mediaUrl'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    BAvatar,
    VueApexCharts,
    vSelect,
    VendorDatePicker,
  },
  data() {
    return {
      selectedAdOption: null,
      selectedDate: '',
      adOptions: [
        { id: null, name: 'Select Ad', logo: '' },
      ],
      totals: {
        views: 1200,
        clicks: 50,
        followers: 1500,
      },
      series: [
        {
          name: 'Views',
          data: [20, 40, 30, 70, 40, 60, 50, 140, 120, 100, 160, 220],
        },
        {
          name: 'Clicks',
          data: [80, 120, 90, 170, 130, 160, 140, 240, 220, 180, 260, 360],
        },
        {
          name: 'Followers',
          data: [200, 220, 210, 250, 220, 240, 230, 320, 300, 280, 340, 390],
        },
      ],
      chartOptions: {
        chart: {
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: 0,
        },
        fill: {
          type: 'solid',
          opacity: [0.45, 0.42, 0.4],
        },
        legend: {
          show: false,
        },
        xaxis: {
          categories: ['7/12', '8/12', '9/12', '10/12', '11/12', '12/12', '13/12', '14/12', '15/12', '16/12', '17/12', '19/12'],
          labels: {
            style: {
              colors: '#9f9bb2',
              fontSize: '12px',
            },
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: '#9f9bb2',
              fontSize: '12px',
            },
          },
          min: 0,
          max: 400,
          tickAmount: 4,
        },
        grid: {
          borderColor: '#f0eef6',
          strokeDashArray: 0,
        },
        tooltip: {
          shared: true,
        },
        colors: ['#2bdac7', '#8fe9c8', '#9bdff5'],
      },
    }
  },
  mounted() {
    this.loadAdOptions()
    this.fetchLastUpdates()
  },
  methods: {
    async loadAdOptions() {
      try {
        const response = await vendorPanelApi.myAds({ page: 1, per_page: 50, search: '' })
        const list = response?.data?.data?.ads || response?.data?.data || []
        const mapped = list.map(item => ({
          id: item.id || item.ad_id || null,
          name: item.ad_name || item.title || `Ad #${item.id || ''}`.trim(),
          logo: resolveMediaUrl(item.thumbnail || item.thambnail || item.image || item.company_logo || item.logo || ''),
        }))
        this.adOptions = [{ id: null, name: 'Select Ad', logo: '' }, ...mapped]
      } catch (e) {
        this.adOptions = [{ id: null, name: 'Select Ad', logo: '' }]
      } finally {
        this.selectedAdOption = this.adOptions[0]
      }
    },
    async fetchLastUpdates() {
      try {
        const response = await vendorPanelApi.lastUpdates({
          id: this.selectedAdOption?.id,
          date: this.selectedDate,
        })
        const data = response?.data?.data || {}
        const summary = data.summary || {}
        this.totals = {
          views: summary.total_views || 0,
          clicks: summary.total_clicks || 0,
          followers: summary.total_followers || 0,
        }
        const rows = data.last_updates || []
        this.series = [
          { name: 'Views', data: rows.map(item => item.views || 0) },
          { name: 'Clicks', data: rows.map(item => item.clicks || 0) },
          { name: 'Followers', data: rows.map(item => item.followers || 0) },
        ]
        this.chartOptions = {
          ...this.chartOptions,
          xaxis: {
            ...this.chartOptions.xaxis,
            categories: rows.map(item => item.date || ''),
          },
        }
      } catch (error) {
        // Keep fallback chart
      }
    },
    openOverallDetails() {
      this.$router.push({ name: 'dashboard-overall-details' })
    },
  },
  watch: {
    selectedAdOption() {
      this.fetchLastUpdates()
    },
    selectedDate() {
      this.fetchLastUpdates()
    },
  },
}
</script>

<style scoped lang="scss">
.dashboard-graph-card {
  border: 0;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.08);
  overflow: hidden;
}

.graph-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1rem 1.5rem;
  align-items: center;
  padding: 1.2rem 1.5rem 0.85rem;
}

.graph-title-wrap {
  flex-shrink: 0;
}

.graph-title {
  color: #4a4a5c;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.graph-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.25rem 1.75rem;
  flex-wrap: wrap;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  color: #5c5869;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 0.4rem;
  flex-shrink: 0;
}

.dot-views {
  background: #2bdac7;
}

.dot-clicks {
  background: #8fe9c8;
}

.dot-followers {
  background: #9bdff5;
}

.graph-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.graph-actions ::v-deep .custom-select,
.graph-actions ::v-deep .form-control {
  min-height: 38px;
  padding: 0.4rem 0.75rem;
  font-size: 13px;
  font-weight: 500;
  color: #5c5869;
  background-color: #fff;
  border: 1px solid #e2e0eb;
  border-radius: 8px;
  box-shadow: none;
}

.graph-actions ::v-deep .custom-select:focus,
.graph-actions ::v-deep .form-control:focus {
  border-color: #c5c2d6;
  box-shadow: 0 0 0 2px rgba(10, 46, 87, 0.06);
}

.action-input {
  min-width: 0;
}

.select-ad {
  min-width: 148px;
  max-width: 200px;
}

.date-input {
  width: 132px;
  flex-shrink: 0;
}

.graph-chart-body {
  padding-left: 1.25rem;
  padding-right: 1.25rem;
  padding-bottom: 1rem;

  @media (min-width: 768px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

.graph-click-area {
  cursor: pointer;
}

@media (max-width: 1200px) {
  .graph-header {
    grid-template-columns: 1fr;
    align-items: flex-start;
    gap: 1rem;
  }

  .graph-legend {
    justify-content: flex-start;
  }

  .graph-actions {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
